/**
 * RuleWeightControls — the numbers of a `'weight'` rule: Static pin / Dynamic
 * boost pills, the shared-total percent, a full-sentence caption saying what
 * will happen, and the inline error/warning line for this card.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import type { FlowLayer, RuleWeightConfig } from '@trafficpulse/shared';

import { NumberField, SegmentedPills } from '@/components/ui';
import { cn } from '@/lib/utils';

import { WEIGHT_MODE_OPTIONS, weightCaption } from './rule-weight-copy';
import type { WeightIssue } from './weight-issues';

export interface RuleWeightControlsProps {
  weight: RuleWeightConfig;
  appliesTo: ReadonlyArray<FlowLayer>;
  onChange: (next: RuleWeightConfig) => void;
  issue?: WeightIssue;
}

const IssueLine: FC<{ issue: WeightIssue }> = ({ issue }) => {
  const error = issue.level === 'error';
  const Icon = error ? AlertCircle : AlertTriangle;
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 rounded-md border px-2 py-1 text-[12px] leading-snug',
        error
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-warning/30 bg-warning/10 text-muted-foreground',
      )}
    >
      <Icon
        className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', error ? '' : 'text-warning')}
        aria-hidden
      />
      <span>{issue.text}</span>
    </p>
  );
};

export const RuleWeightControls: FC<RuleWeightControlsProps> = ({
  weight,
  appliesTo,
  onChange,
  issue,
}) => (
  <div className="space-y-1.5 pl-6">
    <div className="flex flex-wrap items-center gap-2.5">
      <SegmentedPills
        options={WEIGHT_MODE_OPTIONS}
        value={weight.mode}
        onChange={(mode) => onChange({ ...weight, mode })}
      />
      <span className="inline-flex items-center gap-1 text-[13px] text-muted-foreground">
        {weight.mode === 'dynamic' ? (
          <span className="font-semibold text-foreground">+</span>
        ) : null}
        <NumberField
          value={weight.percent}
          onChange={(percent) => onChange({ ...weight, percent })}
          min={1}
          max={100}
          aria-label="Weight percent (shared total)"
          className="h-6 w-[3.5rem] rounded border border-input bg-background px-1.5 text-right text-[12.5px] tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <span className="font-medium text-foreground">
          {weight.mode === 'static' ? '% of the pool' : 'points'}
        </span>
        <span>shared by all matches</span>
      </span>
    </div>
    <p className="text-[12px] leading-snug text-muted-foreground">
      {weightCaption(weight, appliesTo)}
    </p>
    {issue ? <IssueLine issue={issue} /> : null}
  </div>
);

export default RuleWeightControls;
